// "My Stats" -- a self-service page showing the logged-in agent's own AHT
// and contact counts, two ways: week over week (last 12 complete weeks) and
// month over month (last 3 complete months + the current, in-progress one),
// both truncated to whenever THIS agent's own contact history starts (see
// the coverage-start note below). Rendered only for non-elevated agents;
// App.jsx gates both the nav-rail entry and this page's render branch on
// !effectiveIsElevated, since admins already have the full admin Reporting
// page across every agent. No picker over other agents by design.
//
// Widened 2026-10-05 (Patrick's call) from eBay-only to the same six
// accounts the company-wide "Email AHT" blend pools (1A/AM eBay + Internal
// + Website) -- see getMyStatsReport's own comment in reporting.js. Column
// order here is exactly the order the server already returns `data.accounts`
// in (1A eBay, AM eBay, 1A Email, AM Email, then Support Email only for an
// agent who's actually handled one) -- no client-side reordering needed.
import { useEffect, useState } from 'react'
import { Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, CircularProgress, Alert } from '@mui/material'
import { fetchMyStats, fetchMyStatsMonthly } from './api.js'
import { CHANNEL_LABELS } from './constants/channels.js'
import { periodUtilization, totalUtilization } from './myStatsUtilization.js'
import { hasBpEmail, periodBpEmail, totalBpEmail } from './myStatsBpEmail.js'

const nf = new Intl.NumberFormat('en-US')
function fmtCount(n) {
  return nf.format(n ?? 0)
}

// Same m:ss / h:mm:ss convention ReportingPage.jsx's formatHandleMs and
// ConversationActivity.jsx's per-contact display use -- duplicated locally
// rather than shared, matching this codebase's existing tolerance for small
// pure-formatter duplication over a new cross-file coupling for a couple of
// one-off functions (see ui/src/handleTimeAggregate.js's own module comment
// for the same reasoning).
function formatHandleMs(ms) {
  if (typeof ms !== 'number' || !isFinite(ms)) return '—'
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

// ADH% (schedule adherence, from Schedule Hub) as a fraction (0-1) ->
// "98.43%" -- two decimal places, per request (Jeanie Oeurnuch's real
// weekly numbers were close enough together that whole-percent rounding
// was hiding real week-to-week movement). `data.adherence` is null
// whenever Schedule Hub is disabled or the request failed (see
// routes/my-stats.js's graceful-degradation comment) -- callers should
// already be passing undefined/null through in that case, which this
// renders the same as a null period (no data), not 0%.
function formatPercent(fraction) {
  if (typeof fraction !== 'number' || !isFinite(fraction)) return '—'
  return `${(fraction * 100).toFixed(2)}%`
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// 'YYYY-MM' -> "Oct 2026" -- the year is kept (unlike the Power BI report
// this table is modeled after, which never crosses a year boundary within
// its fixed 3-month window) since this one always includes the CURRENT
// month, so a January view's window can cross into the prior year.
function formatMonthLabel(label) {
  const [y, m] = label.split('-').map(Number)
  return `${MONTH_NAMES[m - 1]} ${y}`
}

const CELL_BORDER = { borderRight: '1px solid', borderColor: 'divider' }

// A TRUE average across every period shown -- reconstructs each period's
// total ms from its already-divided average (ms = avg * count, both already
// on hand from the API response) and pools those totals before dividing
// once, same "pool the raw values, divide once" rule getMyStatsReport
// itself follows server-side. Never an average of the per-period averages,
// which would weight a slow 2-contact period the same as a busy 40-contact
// one.
function totalAht(ahtByPeriod, contactsByPeriod, periods) {
  let sumMs = 0
  let count = 0
  for (const p of periods) {
    const c = contactsByPeriod[p] ?? 0
    if (c > 0) {
      sumMs += (ahtByPeriod[p] ?? 0) * c
      count += c
    }
  }
  return count > 0 ? sumMs / count : null
}

function totalContacts(contactsByPeriod, periods) {
  return periods.reduce((sum, p) => sum + (contactsByPeriod[p] ?? 0), 0)
}

const accountLabel = (a) => a.label ?? `${a.company} ${CHANNEL_LABELS[a.channel] ?? a.channel}`

// Shared by both tables below -- same columns (CXHub Email AHT, Total CXHub Contacts,
// one per data.accounts entry in the order the server already returns them,
// then ADH%), just a different period key/label and a different row-label
// formatter (the raw week-ending day label vs. a "Oct 2026"-style month
// label).
function StatsTable({ periodHeader, periods, formatPeriod, data }) {
  if (periods.length === 0) return null

  const combinedTotalAht = totalAht(data.combined.aht, data.combined.contacts, periods)
  const combinedTotalContacts = totalContacts(data.combined.contacts, periods)
  const accountTotals = data.accounts.map((a) => ({
    account: a.account,
    aht: totalAht(a.aht, a.contacts, periods),
  }))
  // Bright Pattern email columns, only for an agent with rows entered from
  // Power BI (the Support team), see myStatsBpEmail.js.
  const showBp = hasBpEmail(data)
  const bpTotal = showBp ? totalBpEmail(data, periods) : null
  const NUM = { ...CELL_BORDER, fontVariantNumeric: 'tabular-nums' }
  const BOLD_NUM = { ...NUM, fontWeight: 700 }

  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={CELL_BORDER}>{periodHeader}</TableCell>
            <TableCell sx={{ ...CELL_BORDER, width: 110 }} align="right">Total CXHub Contacts</TableCell>
            <TableCell sx={{ ...CELL_BORDER, width: 110 }} align="right">CXHub Email AHT</TableCell>
            {showBp && (
              <>
                <TableCell sx={{ ...CELL_BORDER, width: 100 }} align="right">BP Emails</TableCell>
                <TableCell sx={{ ...CELL_BORDER, width: 100 }} align="right">BP Email AHT</TableCell>
                <TableCell sx={{ ...CELL_BORDER, width: 110, fontWeight: 700 }} align="right">Combined Email AHT</TableCell>
              </>
            )}
            {data.accounts.map((a) => (
              <TableCell key={`${a.account}-h`} sx={{ ...CELL_BORDER, width: 100 }} align="right">
                {accountLabel(a)} AHT
              </TableCell>
            ))}
            <TableCell sx={{ ...CELL_BORDER, width: 100 }} align="right">Utilization</TableCell>
            <TableCell sx={{ ...CELL_BORDER, width: 90 }} align="right">ADH%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {periods.map((p) => (
            <TableRow key={p} hover>
              <TableCell sx={CELL_BORDER}>{formatPeriod(p)}</TableCell>
              <TableCell align="right" sx={{ ...CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                {fmtCount(data.combined.contacts[p])}
              </TableCell>
              <TableCell align="right" sx={{ ...CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                {formatHandleMs(data.combined.aht[p])}
              </TableCell>
              {showBp && (() => {
                const bp = periodBpEmail(data, p)
                return (
                  <>
                    <TableCell align="right" sx={NUM}>{bp.bpEmails == null ? '—' : fmtCount(bp.bpEmails)}</TableCell>
                    <TableCell align="right" sx={NUM}>{formatHandleMs(bp.bpAht)}</TableCell>
                    <TableCell align="right" sx={BOLD_NUM}>{formatHandleMs(bp.combinedAht)}</TableCell>
                  </>
                )
              })()}
              {data.accounts.map((a) => (
                <TableCell key={`${a.account}-${p}`} align="right" sx={{ ...CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                  {formatHandleMs(a.aht[p])}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ ...CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                {formatPercent(periodUtilization(data, p))}
              </TableCell>
              <TableCell align="right" sx={{ ...CELL_BORDER, fontVariantNumeric: 'tabular-nums' }}>
                {formatPercent(data.adherence?.points?.[p])}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ ...CELL_BORDER, fontWeight: 700 }}>Total</TableCell>
            <TableCell align="right" sx={{ ...CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {fmtCount(combinedTotalContacts)}
            </TableCell>
            <TableCell align="right" sx={{ ...CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {formatHandleMs(combinedTotalAht)}
            </TableCell>
            {showBp && (
              <>
                <TableCell align="right" sx={BOLD_NUM}>{fmtCount(bpTotal.bpEmails)}</TableCell>
                <TableCell align="right" sx={BOLD_NUM}>{formatHandleMs(bpTotal.bpAht)}</TableCell>
                <TableCell align="right" sx={BOLD_NUM}>{formatHandleMs(bpTotal.combinedAht)}</TableCell>
              </>
            )}
            {accountTotals.map((a) => (
              <TableCell key={`${a.account}-total`} align="right" sx={{ ...CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatHandleMs(a.aht)}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ ...CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {formatPercent(totalUtilization(data, periods))}
            </TableCell>
            <TableCell align="right" sx={{ ...CELL_BORDER, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {formatPercent(data.adherence?.total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  )
}

export default function MyStatsPage() {
  const [weekly, setWeekly] = useState(null)
  const [monthly, setMonthly] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    Promise.all([fetchMyStats(), fetchMyStatsMonthly()])
      .then(([w, m]) => { if (alive) { setWeekly(w); setMonthly(m) } })
      .catch((err) => { if (alive) setError(err.message ?? 'Failed to load') })
    return () => { alive = false }
  }, [])

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Couldn't load your stats: {error}</Alert>
      </Box>
    )
  }
  if (!weekly || !monthly) {
    return (
      <Box sx={{ p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  // Truncate the fixed window to whenever THIS agent's own contact history
  // actually starts -- a blanket 12 weeks (or 4 months) would show a run of
  // empty/zero periods for anyone newer than that, same "coverage-start"
  // reasoning already used for Online-vs-Active and Handle Time in the
  // admin Reporting page. Both responses carry the SAME coverageStartsAt
  // (one agent, one history) -- either would do.
  const coverageStartDate = weekly.coverageStartsAt ? weekly.coverageStartsAt.slice(0, 10) : null
  const coverageStartMonth = coverageStartDate ? coverageStartDate.slice(0, 7) : null
  // weekLabels/monthly's own weekLabels come back most-recent-first (server
  // convention, shared with the admin Reporting page); reversed here to
  // oldest-first, matching the Power BI report this page is modeled after.
  const weeks = coverageStartDate ? weekly.weekLabels.filter((w) => w >= coverageStartDate).reverse() : []
  const months = coverageStartMonth ? monthly.weekLabels.filter((m) => m >= coverageStartMonth).reverse() : []

  return (
    <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>My Stats</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Your own handle time and contact volume, across every account you've handled (Sunday–Saturday weeks, labeled
        by their ending Saturday). Total CXHub Contacts and CXHub Email AHT cover your CXHub work only, every account below
        {weekly?.emailIncludesSupport || monthly?.emailIncludesSupport ? ' including Support Email' : ' except Support Email'}. CXHub Email AHT is a true average (total time divided by total contacts), not an average of
        the individual account numbers. Support Email only appears
        if you've handled one of those contacts. ADH% (schedule adherence, from Schedule Hub) is a true average the
        same way — pooled scheduled/adherent time across every period shown, not an average of period percentages —
        and reads as "—" for any period without adherence data yet. Utilization is the share of your CXHub scheduled time
        (Schedule Hub, completed days only) that went on handling contacts: handle time across every account, Support Email included, divided
        by that scheduled time, pooled the same way. It reads as "—" for a period with no CXHub time scheduled.
      </Typography>
      {hasBpEmail(weekly) || hasBpEmail(monthly) ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          BP Emails and BP Email AHT are your Bright Pattern email results from Power BI (in-focus time), entered by
          your supervisor, so a period reads as "—" until they are entered. Combined Email AHT is every email you
          handled in both systems, Bright Pattern plus every CXHub account above (Support Email included): total time
          divided by total emails. It shows once a period's Bright Pattern numbers are in, and its Total covers those
          periods only.
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {coverageStartDate
          ? `Your contact history begins the week ending ${coverageStartDate} — earlier periods aren't shown.`
          : "No contact history recorded yet — check back once you've claimed and completed some conversations."}
      </Typography>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Last 12 Weeks</Typography>
      <StatsTable periodHeader="Week Ending" periods={weeks} formatPeriod={(p) => p} data={weekly} />

      <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Last 3 Months</Typography>
      <StatsTable periodHeader="Month" periods={months} formatPeriod={formatMonthLabel} data={monthly} />
    </Box>
  )
}
